import { cx } from "cva";
import { createContext, forwardRef, useCallback, useContext } from "react";
import type { ComponentPropsWithoutRef, ForwardedRef, PropsWithChildren } from "react";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { AddIcon, MinimizeIcon } from "@knitto-ui/react-icons";
import Collapsible, { type CollapsibleProps } from "../Collapsible";
import Typography from "../Typography";

type AccordionValue<TIsMultiple extends boolean> = TIsMultiple extends true ? string[] : string;
type AccordionCollapsible<TIsMultiple extends boolean> = TIsMultiple extends true
  ? undefined
  : boolean;

type AccordionValueContextValue = {
  value: string[];
  select(value: string): void;
  hideExpandIcon?: boolean;
};

export const AccordionContext = createContext(null as AccordionValueContextValue | null);

export type AccordionProps<TIsMultiple extends boolean = false> =
  ComponentPropsWithoutRef<"div"> & {
    className?: string;
    collapsible?: AccordionCollapsible<TIsMultiple>;
    defaultValue?: AccordionValue<TIsMultiple>;
    multiple?: TIsMultiple;
    value?: AccordionValue<TIsMultiple>;
    onValueChange?(value?: AccordionValue<TIsMultiple>): void;
    hideExpandIcon?: boolean;
  };

const parseValue = (value?: AccordionValue<boolean>) => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value;
  return;
};

// TODO: implement keyboard navigation
// TODO: implement dark mode
// TODO: implement icon customization
export const Accordion = forwardRef(
  <TIsMultiple extends boolean = false>(
    {
      children,
      className,
      collapsible,
      multiple = false as TIsMultiple,
      value: valueProp,
      defaultValue,
      onValueChange,
      hideExpandIcon,
      ...props
    }: PropsWithChildren<AccordionProps<TIsMultiple>>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [value = [], setValue] = useControllableState({
      prop: parseValue(valueProp),
      defaultProp: parseValue(defaultValue),
      onChange: (value) => {
        return onValueChange?.(multiple ? value : (value?.[0] as any));
      },
    });

    const toggleValue = useCallback((prevValue: string[] = [], newValue: string) => {
      if (prevValue?.includes(newValue)) {
        return prevValue.filter((v) => v !== newValue);
      }
      return [...prevValue, newValue];
    }, []);

    const collapseValue = useCallback((prevValue: string[] | undefined, newValue: string) => {
      if (prevValue?.includes(newValue)) {
        return [];
      }
      return [newValue];
    }, []);

    return (
      <AccordionContext.Provider
        value={{
          value,
          select: (newValue: string) => {
            if (multiple) {
              setValue((prevValue) => toggleValue(prevValue, newValue));
            } else if (collapsible) {
              setValue((prevValue) => collapseValue(prevValue, newValue));
            } else {
              setValue([newValue]);
            }
          },
          hideExpandIcon,
        }}
      >
        <div {...props} ref={ref} className={cx("kn-accordion", className)}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  },
);

Accordion.displayName = "Accordion";

export type AccordionItemProps = ComponentPropsWithoutRef<"div"> & {
  value: string;
};

type AccordionItemContextValue = {
  value: string;
  open: boolean;
};

const AccordionItemContext = createContext(null as AccordionItemContextValue | null);

export const AccordionItem = forwardRef<HTMLDivElement, PropsWithChildren<AccordionItemProps>>(
  ({ children, className, value, ...props }, ref) => {
    const accordionContext = useContext(AccordionContext);

    return (
      <AccordionItemContext.Provider
        value={{
          open: !!accordionContext?.value.includes(value),
          value,
        }}
      >
        <div {...props} ref={ref} className={cx("kn-accordion-item", className)}>
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  },
);

AccordionItem.displayName = "AccordionItem";

export type AccordionTriggerProps = ComponentPropsWithoutRef<"button"> & {
  wrapperClassName?: string;
  expandIconClassName?: string;
  hideExpandIcon?: boolean;
};

export const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<AccordionTriggerProps>
>(
  (
    {
      children,
      className,
      wrapperClassName,
      expandIconClassName,
      hideExpandIcon: hideExpandIconProp,
      ...props
    },
    ref,
  ) => {
    const accordionContext = useContext(AccordionContext);
    const accordionItemContext = useContext(AccordionItemContext);
    const value = accordionItemContext?.value;
    const isOpen = !!accordionItemContext?.open;
    const hideExpandIcon = hideExpandIconProp ?? accordionContext?.hideExpandIcon;
    return (
      <div className={cx("kn-accordion-header", wrapperClassName)}>
        <button
          {...props}
          ref={ref}
          className={cx("kn-accordion-trigger", isOpen && "kn-accordion-trigger-active", className)}
          onClick={() => {
            value && accordionContext?.select?.(value);
          }}
        >
          <Typography as="span" variant="paragraphBold">
            {children}
          </Typography>
          {hideExpandIcon ? null : (
            <div className={cx("kn-accordion-expand-icon-container", expandIconClassName)}>
              <AddIcon
                className={cx("kn-accordion-expand-icon", isOpen ? "opacity-0" : "opacity-100")}
                width={22}
                height={22}
                aria-hidden
              />
              <MinimizeIcon
                className={cx("kn-accordion-expand-icon", isOpen ? "opacity-100" : "opacity-0")}
                width={22}
                height={22}
                aria-hidden
              />
            </div>
          )}
        </button>
      </div>
    );
  },
);

AccordionTrigger.displayName = "AccordionTrigger";

export type AccordionContentProps = Omit<CollapsibleProps, "expanded"> & {
  textClassName?: string;
};

export const AccordionContent = forwardRef<
  HTMLDivElement,
  PropsWithChildren<AccordionContentProps>
>(({ children, className, innerClassName, textClassName, ...props }, ref) => {
  const accordionItemContext = useContext(AccordionItemContext);
  const expanded = !!accordionItemContext?.open;
  return (
    <Collapsible
      {...props}
      ref={ref}
      className={cx("kn-accordion-content", className)}
      innerClassName={cx("kn-accordion-content-inner", innerClassName)}
      expanded={expanded}
    >
      <Typography as="span" variant="paragraph" className={textClassName}>
        {children}
      </Typography>
    </Collapsible>
  );
});
